import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import logoImage from "../assets/LU-2026--Main-Revised.png";
import { LU_RED } from "../theme/luTheme";

/** Original home page palette — matches legacy embedded styles */
const HOME_COLORS = {
  text: "#0a0a0a",
  muted: "#64748b",
  accent: LU_RED,
  divider: "#e2e8f0",
};

const PREVIEW_CLASS = "portal-preview-label";

const PreviewLabel = styled(Typography)({
  marginTop: 14,
  color: HOME_COLORS.accent,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.05em",
  opacity: 0,
  transform: "translateY(6px)",
  transition: "opacity 0.2s ease, transform 0.2s ease",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  "@media (max-width: 899.95px)": {
    display: "none",
  },
});

const PortalCardRoot = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.paper,
  border: "1px solid rgba(2, 6, 23, 0.1)",
  boxShadow: "none",
  [theme.breakpoints.up("md")]: {
    minHeight: 200,
    boxShadow: "0 12px 32px rgba(2, 6, 23, 0.07)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "@media (hover: hover)": {
      [`&:hover .${PREVIEW_CLASS}`]: {
        opacity: 1,
        transform: "translateY(0)",
      },
      "&:hover": {
        transform: "translateY(-8px)",
        boxShadow: "0 20px 44px rgba(2, 6, 23, 0.11)",
      },
    },
  },
}));

const resetPageScroll = () => {
  const html = document.documentElement;
  const body = document.body;
  const root = document.getElementById("root");

  html.classList.remove("portal-scroll-enabled");
  body.classList.remove("portal-scroll-enabled");
  root?.classList.remove("portal-scroll-enabled");

  [html, body, root].forEach((el) => {
    if (!el) return;
    el.style.overflow = "";
    el.style.height = "";
    el.style.minHeight = "";
    el.style.maxHeight = "";
    el.style.position = "";
  });

  body.style.webkitOverflowScrolling = "touch";
};

const setHomeScrollbarHidden = (hidden) => {
  document.documentElement.classList.toggle("hide-scrollbar", hidden);
  document.body.classList.toggle("hide-scrollbar", hidden);
};

const PortalCard = ({ ind, onNavigate }) => (
  <PortalCardRoot onClick={() => onNavigate(`/${ind.id}`)}>
    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: { xs: 2, md: 3 }, textAlign: "left" }}>
      <Typography component="span" sx={{ fontSize: { xs: 24, md: 30 }, opacity: 0.75, lineHeight: 1 }}>
        {ind.icon}
      </Typography>
      <Typography variant="h6" sx={{ mt: 1.75, mb: 1, fontWeight: 800, fontSize: { xs: "0.9rem", md: "1rem" }, color: HOME_COLORS.text }}>
        {ind.name} Portal
      </Typography>
      <Typography sx={{ flex: 1, lineHeight: 1.55, fontSize: 14, color: HOME_COLORS.muted }}>
        {ind.desc}
      </Typography>
      <PreviewLabel className={PREVIEW_CLASS}>PREVIEW DOMAIN <ArrowRightAltIcon fontSize="inherit" sx={{ strokeWidth: 1 }} /></PreviewLabel>
    </CardContent>
  </PortalCardRoot>
);

const FuturisticHome = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    resetPageScroll();
    setHomeScrollbarHidden(true);
    window.scrollTo(0, 0);

    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      setHomeScrollbarHidden(false);
    };
  }, []);

  const scrollToSection = (id) => {
    setMobileNavOpen(false);
    const section = document.getElementById(id);
    if (!section) return;
    const top = section.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const industries = [
    { id: "finance", name: "Finance", icon: "🏛️", desc: "Institutional grade biometric authorization for digital banking." },
    { id: "healthcare", name: "Healthcare", icon: "🏥", desc: "Private, high-trust patient identity and medical record gateways." },
    { id: "insurance", name: "Insurance", icon: "🛡️", desc: "Continuous identity verification for multi-layer platform security." },
    { id: "government", name: "Government", icon: "⚖️", desc: "Citizen-centric identity nodes with national-grade protection." },
    { id: "corporate", name: "Corporate", icon: "🏢", desc: "Unifying global workforce access via a single biometric signature." },
  ];

  const integrations = [
    { step: "01", title: "Easy Integration", desc: "Integrate high-trust biometric access with a clean API." },
    { step: "02", title: "AI Core", desc: "Sub-second spatial verification." },
    { step: "03", title: "Seamless Token", desc: "Cryptographic proof of identity." },
  ];

  const navItems = [
    { label: "Portals", id: "platforms", active: true },
    { label: "Integration", id: "solutions", active: false },
  ];

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          bgcolor: scrolled ? "rgba(255,255,255,0.92)" : "transparent",
          color: HOME_COLORS.text,
          py: { xs: 0, md: scrolled ? 0.5 : 1.25 },
          transition: "background 0.25s ease, padding 0.25s ease",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 6 }, minHeight: { xs: 56, md: 64 } }}>
          <Box onClick={() => navigate("/")} sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
            <Box component="img" src={logoImage} alt="Legitimately U" sx={{ height: { xs: 32, md: 36 } }} />
          </Box>
          <Stack direction="row" spacing={{ xs: 1, md: 4.5 }} sx={{ alignItems: "center" }}>
            <Stack direction="row" spacing={{ md: 4.5 }} sx={{ display: { xs: "none", sm: "flex" } }}>
              {navItems.map((it) => (
                <Link
                  key={it.label}
                  href={`#${it.id}`}
                  underline="none"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(it.id);
                  }}
                  sx={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: it.active ? HOME_COLORS.accent : HOME_COLORS.text,
                  }}
                >
                  {it.label}
                </Link>
              ))}
            </Stack>
            <IconButton
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
              sx={{ display: { xs: "inline-flex", sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => window.open("https://web.legitimatelyu.com/", "_blank")}
              sx={{ textTransform: "uppercase", letterSpacing: "0.03em", px: { xs: 2, sm: 2.5 }, fontSize: { xs: 11, sm: 13 } }}
            >
              Get Started
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          paper: {
            sx: {
              width: "min(320px, 88vw)",
              pt: "env(safe-area-inset-top)",
              pb: "env(safe-area-inset-bottom)",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, minHeight: 56, borderBottom: 1, borderColor: "divider" }}
        >
          <Box component="img" src={logoImage} alt="Legitimately U" sx={{ height: 28 }} />
          <IconButton aria-label="Close menu" onClick={() => setMobileNavOpen(false)} edge="end" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Typography
          variant="overline"
          sx={{ px: 2.5, pt: 2.5, pb: 0.5, color: "text.secondary", fontWeight: 700, letterSpacing: "0.1em", fontSize: 11 }}
        >
          Navigate
        </Typography>
        <List disablePadding sx={{ px: 1 }}>
          {navItems.map((it) => (
            <ListItemButton
              key={it.id}
              onClick={() => scrollToSection(it.id)}
              sx={{ mx: 0.5, borderRadius: 1.5, py: 1.25, "&:hover": { bgcolor: "grey.50" } }}
            >
              <ListItemText
                primary={it.label}
                slotProps={{
                  primary: {
                    fontWeight: 600,
                    fontSize: 15,
                    color: it.active ? HOME_COLORS.accent : HOME_COLORS.text,
                  },
                }}
              />
              <ChevronRightIcon sx={{ fontSize: 18, color: "text.disabled" }} />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ flex: 1 }} />

        <Stack spacing={1.5} sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => {
              setMobileNavOpen(false);
              window.open("https://web.legitimatelyu.com/", "_blank");
            }}
            sx={{ py: 1.25, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", fontSize: 13 }}
          >
            Get Started
          </Button>
        </Stack>
      </Drawer>

      <Box component="main" sx={{ bgcolor: "#ffffff", color: HOME_COLORS.text }}>
        <Container
          maxWidth={false}
          sx={{
            maxWidth: { md: 1100 },
            px: { xs: 2, sm: 3, md: 6 },
            pt: { xs: 2, md: 2 },
            pb: 0,
          }}
        >
          <Box component="section" sx={{ textAlign: "center", pt: { xs: 1, md: 3 }, pb: { xs: 2, md: 0 } }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "1.75rem", sm: "2.4rem", md: "3.5rem" },
                fontWeight: 900,
                lineHeight: 1.08,
                letterSpacing: "-0.02em",
                mb: 2.5,
                color: HOME_COLORS.text,
              }}
            >
              One Identity.
              <br />
              <Box component="span" sx={{ color: HOME_COLORS.accent }}>
                Unified Secure Access.
              </Box>
            </Typography>
            <Typography
              sx={{ maxWidth: 680, mx: "auto", fontSize: 15, lineHeight: 1.65, color: HOME_COLORS.muted }}
            >
              This webpage demonstrates 5 portals which can use LU App for authentication. These are just a few
              examples and many more use cases exist.
            </Typography>
            <Box sx={{ width: 72, height: "1px", bgcolor: HOME_COLORS.divider, mx: "auto", mt: 4.5 }} />
          </Box>

          <Box component="section" id="platforms" sx={{ pt: { xs: 3, md: 5.5 }, pb: { xs: 4, md: 0 } }}>
            <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" } }}>
              {industries.map((ind) => (
                <PortalCard key={ind.id} ind={ind} onNavigate={navigate} />
              ))}
            </Stack>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 3 }}>
                {industries.slice(0, 3).map((ind) => (
                  <PortalCard key={ind.id} ind={ind} onNavigate={navigate} />
                ))}
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 3, mt: 3 }}>
                {industries.slice(3).map((ind, i) => (
                  <Box key={ind.id} sx={{ gridColumn: i === 0 ? "2 / 4" : "4 / 6" }}>
                    <PortalCard ind={ind} onNavigate={navigate} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box component="section" id="solutions" sx={{ py: { xs: 4, md: 9 }, pb: { md: 7 } }}>
            <Stack spacing={2.5} sx={{ display: { xs: "flex", md: "none" } }}>
              {integrations.map((s) => (
                <Box key={s.step}>
                  <Typography variant="caption" sx={{ color: HOME_COLORS.accent, fontWeight: 800, letterSpacing: "0.02em", fontSize: 12 }}>
                    {s.step} · INTEGRATION
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1, mb: 0.5, fontWeight: 800, fontSize: "1rem", color: HOME_COLORS.text }}>
                    {s.title}
                  </Typography>
                  <Typography sx={{ lineHeight: 1.55, fontSize: 14, color: HOME_COLORS.muted }}>
                    {s.desc}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: "repeat(3, 1fr)", gap: 5 }}>
              {integrations.map((s) => (
                <Box key={s.step} sx={{ textAlign: "left" }}>
                  <Typography variant="caption" sx={{ color: HOME_COLORS.accent, fontWeight: 800, letterSpacing: "0.02em", fontSize: 12 }}>
                    {s.step} · INTEGRATION
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1.5, mb: 1, fontWeight: 800, fontSize: "1.05rem", color: HOME_COLORS.text }}>
                    {s.title}
                  </Typography>
                  <Typography sx={{ lineHeight: 1.55, fontSize: 14, color: HOME_COLORS.muted }}>
                    {s.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: { xs: 3, md: 0 },
          pb: { xs: "calc(24px + env(safe-area-inset-bottom, 0px))", md: 4.5 },
          px: { md: 6 },
          textAlign: "center",
          bgcolor: "#ffffff",
        }}
      >
        <Typography sx={{ fontSize: 13, color: HOME_COLORS.muted }}>
          © 2026 LegitimatelyU
        </Typography>
      </Box>
    </>
  );
};

export default FuturisticHome;
